/**
 * Return-to-Origin Navigation Utility
 * 
 * Implements a canonical navigation contract for cross-surface flows.
 * When a user navigates away to complete a detour action (e.g., configure criteria),
 * this utility ensures they return to their original surface after completion.
 * 
 * Per Doc 08 (Batch 4):
 * - After any detour action, user returns to where they started
 * - Navigation state is preserved via React Router's location.state
 * - Origin context is typed and validated
 * 
 * Usage:
 * ```typescript
 * // Navigating away with origin
 * const origin = createOrigin(location, { cycleId, tab: 'gather' });
 * navigateWithOrigin(navigate, '/criteria/new', origin);
 * 
 * // Returning after save
 * const { origin } = useOriginState();
 * if (origin) {
 *   returnToOrigin(navigate, origin, { created: newCriterionId });
 * }
 * ```
 */

import { useLocation, useNavigate, type NavigateFunction, type Location } from 'react-router-dom';
import { useMemo, useCallback } from 'react';

// ============================================================================
// Types
// ============================================================================

/**
 * Context about the origin surface.
 * Used to restore state when returning.
 */
export interface OriginContext {
  /** Cycle ID if originating from a cycle-related surface */
  cycleId?: string;
  /** Tab or section within the surface */
  tab?: string;
  /** Specific section within a tab */
  section?: string;
  /** What action the user was performing */
  action?: string;
  /** Surface identifier for analytics/debugging */
  surfaceId?: string;
  /** Any additional context needed for restoration */
  [key: string]: string | undefined;
}

/**
 * Complete navigation origin specification.
 * Passed via React Router's location.state.
 */
export interface NavigationOrigin {
  /** Full route to return to (path + search params) */
  origin_route: string;
  /** Contextual information about the origin */
  origin_context?: OriginContext;
  /** Computed return route (may include state restoration params) */
  return_to: string;
  /** Timestamp when origin was created (for staleness detection) */
  created_at: number;
}

/**
 * Result returned when completing a detour action.
 * Passed back to the origin surface.
 */
export interface ReturnResult {
  /** Whether the action was successful */
  success: boolean;
  /** ID of created/modified entity */
  entityId?: string;
  /** Type of action completed */
  actionType?: 'created' | 'updated' | 'deleted' | 'cancelled';
  /** Any additional result data */
  [key: string]: unknown;
}

/**
 * State shape for navigation with origin.
 */
export interface NavigationStateWithOrigin {
  origin?: NavigationOrigin;
  returnResult?: ReturnResult;
}

// ============================================================================
// Origin Creation
// ============================================================================

/**
 * Creates a NavigationOrigin from the current location.
 * 
 * @param location - Current React Router location
 * @param context - Optional context about the origin surface
 * @returns NavigationOrigin for passing to detour navigation
 */
export function createOrigin(
  location: Location | string,
  context?: OriginContext
): NavigationOrigin {
  const route = typeof location === 'string' 
    ? location 
    : `${location.pathname}${location.search}`;
  
  return {
    origin_route: route,
    origin_context: context,
    return_to: route,
    created_at: Date.now(),
  };
}

/**
 * Creates an origin from current location with computed return route.
 * Adds query params to signal a return from detour.
 * 
 * @param location - Current React Router location
 * @param context - Optional context about the origin surface
 * @returns NavigationOrigin with enhanced return_to
 */
export function createOriginWithReturnSignal(
  location: Location | string,
  context?: OriginContext
): NavigationOrigin {
  const origin = createOrigin(location, context);
  
  // Add return signal to the return route
  const returnUrl = new URL(origin.origin_route, window.location.origin);
  returnUrl.searchParams.set('_returned', 'true');
  if (context?.action) {
    returnUrl.searchParams.set('_from_action', context.action);
  }
  
  return {
    ...origin,
    return_to: returnUrl.pathname + returnUrl.search,
  };
}

// ============================================================================
// Navigation Functions
// ============================================================================

/**
 * Navigate to a detour route while preserving origin.
 * 
 * @param navigate - React Router navigate function
 * @param to - Target route for the detour
 * @param origin - Origin to return to after completing detour
 * @param additionalState - Any additional state to pass
 */
export function navigateWithOrigin(
  navigate: NavigateFunction,
  to: string,
  origin: NavigationOrigin,
  additionalState?: Record<string, unknown>
): void {
  navigate(to, {
    state: {
      origin,
      ...additionalState,
    },
  });
}

/**
 * Return to the origin surface after completing a detour action.
 * 
 * @param navigate - React Router navigate function
 * @param origin - The origin to return to
 * @param result - Optional result of the detour action
 */
export function returnToOrigin(
  navigate: NavigateFunction,
  origin: NavigationOrigin,
  result?: ReturnResult
): void {
  navigate(origin.return_to, {
    state: {
      returnResult: result,
      returnedFrom: origin.origin_context?.action,
    },
    replace: true, // Replace the detour route in history
  });
}

/**
 * Check if an origin is stale (older than threshold).
 * Stale origins should not be used for return navigation.
 * 
 * @param origin - Origin to check
 * @param maxAgeMs - Maximum age in milliseconds (default: 30 minutes)
 * @returns true if origin is stale
 */
export function isOriginStale(
  origin: NavigationOrigin,
  maxAgeMs: number = 30 * 60 * 1000
): boolean {
  return Date.now() - origin.created_at > maxAgeMs;
}

// ============================================================================
// React Hooks
// ============================================================================

/**
 * Hook to access origin state from current location.
 * 
 * @returns Origin and result from navigation state
 */
export function useOriginState(): {
  origin: NavigationOrigin | undefined;
  returnResult: ReturnResult | undefined;
  hasOrigin: boolean;
  isStale: boolean;
} {
  const location = useLocation();
  
  return useMemo(() => {
    const state = location.state as NavigationStateWithOrigin | undefined;
    const origin = state?.origin;
    const returnResult = state?.returnResult;
    
    return {
      origin,
      returnResult,
      hasOrigin: !!origin,
      isStale: origin ? isOriginStale(origin) : false,
    };
  }, [location.state]);
}

/**
 * Hook providing navigation helpers with origin support.
 * 
 * @returns Navigation helpers for detour flows
 */
export function useOriginNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { origin, returnResult, hasOrigin, isStale } = useOriginState();
  
  /**
   * Navigate to a detour route, preserving current location as origin.
   */
  const goToDetour = useCallback((
    to: string,
    context?: OriginContext,
    additionalState?: Record<string, unknown>
  ) => {
    const newOrigin = createOrigin(location, context);
    navigateWithOrigin(navigate, to, newOrigin, additionalState);
  }, [navigate, location]);
  
  /**
   * Return to origin if one exists.
   */
  const goBack = useCallback((result?: ReturnResult) => {
    if (origin && !isStale) {
      returnToOrigin(navigate, origin, result);
      return true;
    }
    return false;
  }, [navigate, origin, isStale]);
  
  /**
   * Return to origin or fallback route.
   */
  const goBackOrFallback = useCallback((
    fallback: string,
    result?: ReturnResult
  ) => {
    if (!goBack(result)) {
      navigate(fallback, { replace: true });
    }
  }, [goBack, navigate]);
  
  return {
    // State
    origin,
    returnResult,
    hasOrigin,
    isStale,
    
    // Actions
    goToDetour,
    goBack,
    goBackOrFallback,
    
    // Low-level helpers
    navigate,
    createOrigin: (context?: OriginContext) => createOrigin(location, context),
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Parse return result from location state.
 * Clears the state after reading to prevent re-processing.
 * 
 * @param location - React Router location
 * @param navigate - React Router navigate function (to clear state)
 * @returns ReturnResult if present, undefined otherwise
 */
export function consumeReturnResult(
  location: Location,
  navigate: NavigateFunction
): ReturnResult | undefined {
  const state = location.state as NavigationStateWithOrigin | undefined;
  const result = state?.returnResult;
  
  if (result) {
    // Clear the state to prevent re-processing on refresh
    navigate(location.pathname + location.search, {
      replace: true,
      state: undefined,
    });
  }
  
  return result;
}

/**
 * Build a return route with context restoration params.
 * 
 * @param baseRoute - Base route to return to
 * @param context - Context to encode in query params
 * @returns Route with context params
 */
export function buildReturnRoute(
  baseRoute: string,
  context?: OriginContext
): string {
  if (!context) return baseRoute;
  
  const url = new URL(baseRoute, window.location.origin);
  
  if (context.tab) {
    url.searchParams.set('tab', context.tab);
  }
  if (context.section) {
    url.searchParams.set('section', context.section);
  }
  
  return url.pathname + url.search;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Common surface IDs for origin tracking.
 */
export const SURFACE_IDS = {
  CYCLE_DETAIL_DECIDE: 'cycle-detail-decide',
  CYCLE_DETAIL_ENGAGE: 'cycle-detail-engage',
  CYCLE_DETAIL_GATHER: 'cycle-detail-gather',
  CYCLE_DETAIL_ARRANGE: 'cycle-detail-arrange',
  CYCLE_DETAIL_PRIORITIZE: 'cycle-detail-prioritize',
  TOOL_PICKER: 'tool-picker',
  MY_TASKS: 'my-tasks',
  DASHBOARD: 'dashboard',
} as const;

/**
 * Common action types for origin context.
 */
export const ACTION_TYPES = {
  ADD_CRITERION: 'add-criterion',
  EDIT_CRITERION: 'edit-criterion',
  DEFINE_SCALES: 'define-scales',
  ACCEPT_SCALES: 'accept-scales',
  CONFIGURE_POOL: 'configure-pool',
  LOCK_POOL: 'lock-pool',
  SCHEDULE_SESSION: 'schedule-session',
  CREATE_TASK: 'create-task',
} as const;
